import { DetectedObject } from "../hooks/useExcelExport";
import { DetectedObjectBoundingBox } from "../types/documentation";

export const drawPhotoReferenceCanvas = (planImage: HTMLImageElement, obj: DetectedObject): string => {
  // Create canvas element
  const canvas = document.createElement('canvas');
  canvas.width = 180;
  canvas.height = 120;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Clear canvas with white background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, 180, 120);

  // Calculate zoom factor to show detail around the object position
  const zoomFactor = 1;

  // Calculate the area of the original image to crop while maintaining aspect ratio
  const canvasAspect = canvas.width / canvas.height; // 2:1
  const imageAspect = planImage.width / planImage.height;

  let cropWidth, cropHeight;

  // Determine crop dimensions based on canvas aspect ratio
  if (imageAspect > canvasAspect) {
    // Image is wider than canvas aspect ratio - fit by height
    cropHeight = canvas.height / zoomFactor;
    cropWidth = cropHeight * canvasAspect;
  } else {
    // Image is taller than canvas aspect ratio - fit by width
    cropWidth = canvas.width / zoomFactor;
    cropHeight = cropWidth / canvasAspect;
  }

  // Center the crop area around the object position (x, y)
  const cropX = Math.max(0, Math.min(planImage.width - cropWidth, obj.position.x - cropWidth / 2));
  const cropY = Math.max(0, Math.min(planImage.height - cropHeight, obj.position.y - cropHeight / 2));

  // Calculate scaling factors for proper aspect ratio maintenance
  const scaleX = canvas.width / cropWidth;
  const scaleY = canvas.height / cropHeight;
  const uniformScale = Math.min(scaleX, scaleY);

  // Calculate actual draw dimensions maintaining aspect ratio
  const drawWidth = cropWidth * uniformScale;
  const drawHeight = cropHeight * uniformScale;
  const offsetX = (canvas.width - drawWidth) / 2;
  const offsetY = (canvas.height - drawHeight) / 2;

  // Draw the cropped and zoomed portion of the image with proper aspect ratio
  ctx.drawImage(
    planImage,
    cropX, cropY, cropWidth, cropHeight,  // Source crop area
    offsetX, offsetY, drawWidth, drawHeight // Destination with proper scaling
  );

  // Calculate the circle position on the canvas maintaining proper ratios
  const relativeX = (obj.position.x - cropX) / cropWidth;
  const relativeY = (obj.position.y - cropY) / cropHeight;
  const circleX = offsetX + (relativeX * drawWidth);
  const circleY = offsetY + (relativeY * drawHeight);

  // Calculate circle radius maintaining proper scale (40px in original image scale)
  const radius = (20 * uniformScale);

  // Draw filled circle
  ctx.beginPath();
  ctx.arc(circleX, circleY, radius, 0, 2 * Math.PI);
  ctx.fillStyle = `rgba(${obj.dwg_rgb[0]}, ${obj.dwg_rgb[1]}, ${obj.dwg_rgb[2]}, 0.3)`;
  ctx.fill();

  // Draw circle border
  ctx.strokeStyle = `rgb(${obj.dwg_rgb[0]}, ${obj.dwg_rgb[1]}, ${obj.dwg_rgb[2]})`; // Red border
  ctx.lineWidth = 3;
  ctx.stroke();

  // Draw border around the entire canvas
  ctx.strokeStyle = '#000000';
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, 180 - 2, 120 - 2);

  // Convert canvas to base64
  return canvas.toDataURL('image/png');
};

export const drawDocumentationReferenceCanvas = (frameImage: HTMLImageElement, bbox: DetectedObjectBoundingBox | undefined, isInstall: boolean): string => {
  // Create canvas element
  const canvas = document.createElement('canvas');
  canvas.width = 180;
  canvas.height = 120;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Clear canvas with white background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, 180, 120);

  // Calculate zoom factor to show detail around the bounding box
  const zoomFactor = 0.2;

  // Calculate bbox dimensions and center
  const bboxWidth = (bbox?.xmax || 0) - (bbox?.xmin || 0);
  const bboxHeight = (bbox?.ymax || 0) - (bbox?.ymin || 0);
  const bboxCenterX = (bbox?.xmin || 0) + bboxWidth / 2;
  const bboxCenterY = (bbox?.ymin || 0) + bboxHeight / 2;

  // Calculate the area of the frame image to crop while maintaining aspect ratio
  const canvasAspect = canvas.width / canvas.height; // 1.5 (180/120)
  const frameAspect = frameImage.width / frameImage.height;

  let cropWidth, cropHeight;

  // Determine crop dimensions based on canvas aspect ratio
  if (frameAspect > canvasAspect) {
    // Frame image is wider than canvas aspect ratio - fit by height
    cropHeight = canvas.height / zoomFactor;
    cropWidth = cropHeight * canvasAspect;
  } else {
    // Frame image is taller than canvas aspect ratio - fit by width
    cropWidth = canvas.width / zoomFactor;
    cropHeight = cropWidth / canvasAspect;
  }

  // Center the crop area around the bbox center
  const cropX = Math.max(0, Math.min(frameImage.width - cropWidth, bboxCenterX - cropWidth / 2));
  const cropY = Math.max(0, Math.min(frameImage.height - cropHeight, bboxCenterY - cropHeight / 2));

  // Calculate scaling factors for proper aspect ratio maintenance
  const scaleX = canvas.width / cropWidth;
  const scaleY = canvas.height / cropHeight;
  const uniformScale = Math.min(scaleX, scaleY);

  // Calculate actual draw dimensions maintaining aspect ratio
  const drawWidth = cropWidth * uniformScale;
  const drawHeight = cropHeight * uniformScale;
  const offsetX = (canvas.width - drawWidth) / 2;
  const offsetY = (canvas.height - drawHeight) / 2;

  // Draw the cropped and zoomed portion of the frame image with proper aspect ratio
  ctx.drawImage(
    frameImage,
    cropX, cropY, cropWidth, cropHeight,  // Source crop area
    offsetX, offsetY, drawWidth, drawHeight // Destination with proper scaling
  );

  // Calculate the bbox position on the canvas maintaining proper ratios
  const bboxRelativeX = (bboxCenterX - cropX) / cropWidth;
  const bboxRelativeY = (bboxCenterY - cropY) / cropHeight;
  const bboxCanvasX = offsetX + (bboxRelativeX * drawWidth);
  const bboxCanvasY = offsetY + (bboxRelativeY * drawHeight);

  // Calculate bbox dimensions on canvas maintaining proper scale
  const bboxScaledWidth = (bboxWidth * uniformScale);
  const bboxScaledHeight = (bboxHeight * uniformScale);

  // Draw bounding box rectangle
  ctx.strokeStyle = isInstall ? 'magenta' : 'orange';
  ctx.lineWidth = 2;
  ctx.strokeRect(
    bboxCanvasX - bboxScaledWidth / 2,
    bboxCanvasY - bboxScaledHeight / 2,
    bboxScaledWidth,
    bboxScaledHeight
  );


  // Draw border around the entire canvas
  ctx.strokeStyle = '#000000';
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, 180 - 2, 120 - 2);

  // Convert canvas to base64
  return canvas.toDataURL('image/png');
};

export const combineImages = async (planBase64: string, frameBase64: string): Promise<string> => {
  // Create a larger canvas to hold both images side by side
  const combinedCanvas = document.createElement('canvas');
  combinedCanvas.width = 360; // 180 + 180
  combinedCanvas.height = 120;
  const ctx = combinedCanvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Clear canvas with white background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, 360, 120);

  try {
    // Create image elements from base64 strings
    const planImg = new Image();
    const frameImg = new Image();

    // Load images asynchronously
    const loadImage = (img: HTMLImageElement, src: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = src;
      });
    };

    // Wait for both images to load
    await Promise.all([
      loadImage(planImg, planBase64),
      loadImage(frameImg, frameBase64)
    ]);

    // Now draw the images
    // Draw plan image on the left side (0, 0, 180, 120)
    ctx.drawImage(planImg, 0, 0, 180, 120);

    // Draw frame image on the right side (180, 0, 180, 120)
    ctx.drawImage(frameImg, 180, 0, 180, 120);

    // Draw separator line
    ctx.strokeStyle = '#cccccc';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(180, 0);
    ctx.lineTo(180, 120);
    ctx.stroke();

    // Draw border around entire canvas
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, 358, 118);

    return combinedCanvas.toDataURL('image/png');
  } catch (error) {
    throw new Error('Error combining images: ' + error);
  }
};
